import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@apollo/client';
import { BarChart } from '@m/alchemy-ui/BarChart';
import { Button } from '@m/alchemy-ui/Button';
import { EmptyState as ErrorMessage } from '@m/alchemy-ui/esm/empty-state/EmptyState';
import { LoadingIndicator } from '@m/alchemy-ui/LoadingIndicator';
import { Select } from '@m/alchemy-ui/Select';
import { ThemeProvider } from '@m/alchemy-ui/theme';
import { useIntl } from 'react-intl';
import {
  type ChallengesQuery,
  type FilteredUsersQuery,
  GET_CHALLENGES,
  GET_COUNTRIES,
  GET_THEME_IDEAS_AND_COUNT,
  GET_USERFIELDS_FILTERED_USERIDS,
  type GetCountriesQuery,
  type GetThemeIdeasAndCountQuery,
  type GetThemeIdeasSummaryQuery
} from '../../graphql/queries';

import { useUserFields, useWindowWidth } from '../../hooks';
import { CommunityContext } from '../../context';
import { GET_THEME_IDEAS_SUMMARY } from '../../graphql/queries/stat';
import {
  ChartActionsRow,
  ChartScroll,
  ChartScrollInner,
  Grid,
  GridCol,
  GridHeader,
  GridRow,
  Pagination
} from './Analytics.styled';

import * as Styled from './components/SearchFilters.styled';
import { accessibleChartTheme, getChartMinWidth, getChartScrollWidth } from './helpers/chartUtils';
import { CustomFilters } from './CustomFilters';
import { type ChallengeListItemType, type ThemeIdeasSummaryItemType, type UserFieldType } from './types';
import { ChartExportMenu, IdeasExportButton, IdeasTable, TableView } from './components';

import { EXPORT_TO_CSV_ENDPOINT, IDEAS_DEFAULT_OFFSET, IDEAS_DEFAULT_SORT, IDEAS_PER_PAGE } from './constants';

import {
  AlchemySortDirectionType,
  ChallengeSelectionType,
  CoreApiSortDirectionType,
  ThemeSelectionType
} from './enums';

export const IdeasByThemeStats = ({ dataTableViewEnabled }: { dataTableViewEnabled: boolean }) => {
  const intl = useIntl();

  const community = useContext(CommunityContext);

  const { data: communityUserFields } = useUserFields();

  const defaultUserFieldOptions: UserFieldType = {};
  const [userFieldOptions, setUserFieldOptions] = useState(defaultUserFieldOptions);
  const [countries, setCountries] = useState<string[]>([]);

  const [challenge, setChallenge] = useState<string>(ChallengeSelectionType.PUBLISHED);
  const [theme, setTheme] = useState<string>(ThemeSelectionType.PUBLISHED);
  const chartContainerRef = useRef<HTMLDivElement>(null);

  const windowWidth = useWindowWidth();

  const { data: countryList } = useQuery<GetCountriesQuery>(GET_COUNTRIES, {
    variables: { id: community.id },
    fetchPolicy: 'cache-first'
  });

  useEffect(() => {
    if (countryList?.community) {
      countryList.community && setCountries(countryList.community.countries as string[]);
    }
  }, [countryList]);

  const { loading: loadingChallenges, data: challengeList } = useQuery<ChallengesQuery>(GET_CHALLENGES, {
    variables: { order: ['title'] },
    fetchPolicy: 'cache-first'
  });

  const { data: customFiltersUserIds } = useQuery<FilteredUsersQuery>(GET_USERFIELDS_FILTERED_USERIDS, {
    variables: { filters: { userFields: JSON.stringify(userFieldOptions) } },
    fetchPolicy: 'cache-first'
  });

  const [themeIdeasSummaryQueryVariables, setThemeIdeasSummaryQueryVariables] = useState({
    challengeSelection: challenge,
    themeSelection: theme,
    filters: { userFields: JSON.stringify(userFieldOptions) }
  });

  const { data: chartData, loading: loadingChart } = useQuery<GetThemeIdeasSummaryQuery>(GET_THEME_IDEAS_SUMMARY, {
    variables: themeIdeasSummaryQueryVariables
  });

  const [themeIdeasQueryVariables, setThemeIdeasQueryVariables] = useState({
    challengeSelection: challenge,
    themeSelection: theme,
    filters: { userFields: JSON.stringify(userFieldOptions) },
    order: [IDEAS_DEFAULT_SORT],
    offset: IDEAS_DEFAULT_OFFSET
  });

  const isThemeSelected = useMemo(
    () => themeIdeasQueryVariables.themeSelection !== ThemeSelectionType.PUBLISHED,
    [themeIdeasQueryVariables]
  );

  const { data: ideasData, loading: loadingIdeas } = useQuery<GetThemeIdeasAndCountQuery>(GET_THEME_IDEAS_AND_COUNT, {
    variables: themeIdeasQueryVariables,
    // If a specific theme is not selected, do not request any ideas data
    skip: !isThemeSelected
  });

  useEffect(() => {
    setThemeIdeasQueryVariables({
      challengeSelection: challenge,
      themeSelection: theme,
      filters: { userFields: JSON.stringify(userFieldOptions) },
      order: [IDEAS_DEFAULT_SORT],
      offset: IDEAS_DEFAULT_OFFSET
    });
  }, [challenge, setThemeIdeasQueryVariables, theme, userFieldOptions]);

  const sortIdeas = (column: string) => {
    const { order } = themeIdeasQueryVariables;
    const [sortColumn, sortDirection] = order[0];
    const newOrder = [
      [
        column,
        column === sortColumn && sortDirection === CoreApiSortDirectionType.ASC
          ? CoreApiSortDirectionType.DESC
          : CoreApiSortDirectionType.ASC
      ]
    ];

    setThemeIdeasQueryVariables({
      ...themeIdeasQueryVariables,
      order: newOrder
    });
  };

  const getColumnSortDirection = (column: string) => {
    const { order } = themeIdeasQueryVariables;
    const [sortColumn, sortDirection] = order[0];
    return sortColumn === column
      ? sortDirection === CoreApiSortDirectionType.DESC
        ? AlchemySortDirectionType.DESC
        : AlchemySortDirectionType.ASC
      : 'none';
  };

  const getExportIdeasUrl = (): URL => {
    let challengeId;

    if (isThemeSelected) {
      challengeId = theme;
    } else {
      challengeId =
        challenge === ChallengeSelectionType.PUBLISHED || challenge === ChallengeSelectionType.ALL ? '0' : challenge;
    }

    const baseUrl = `https://${community.url}`;
    const exportUrl = new URL(EXPORT_TO_CSV_ENDPOINT + String(challengeId), baseUrl);

    exportUrl.searchParams.append('includeUnpublished', challenge === ChallengeSelectionType.PUBLISHED ? '0' : '1');
    exportUrl.searchParams.append('includeComments', '0');

    if (customFiltersUserIds && customFiltersUserIds.filteredUsers.length > 0) {
      exportUrl.searchParams.append('customFiltersUserIds', (customFiltersUserIds?.filteredUsers || []).toString());
    }

    return exportUrl;
  };

  const themeSummary = useMemo(() => chartData?.themeIdeasSummary ?? [], [chartData?.themeIdeasSummary]);
  const ideasLabel = intl.formatMessage({ id: 'ideas', defaultMessage: 'Ideas' });

  const chartSeries = useMemo(
    () => [
      {
        id: ideasLabel,
        data: themeSummary.map(({ ideaCount, themeId, themeName }: ThemeIdeasSummaryItemType) => ({
          x: `${themeName}_${themeId}`, // Make x unique to prevent grouping
          y: ideaCount ?? 0,
          themeId,
          themeName // Store original name for display
        }))
      }
    ],
    [ideasLabel, themeSummary]
  );

  const chartScrollWidth = useMemo(() => getChartScrollWidth(windowWidth), [windowWidth]);

  const chartMinWidth = useMemo(() => getChartMinWidth(chartSeries[0]?.data.length ?? 0), [chartSeries]);

  const formatDate = (date: Date) => date.toISOString().slice(0, 10);

  const tableColumns = useMemo(
    () => [
      { label: intl.formatMessage({ id: 'theme', defaultMessage: 'Theme' }) },
      { label: intl.formatMessage({ id: 'ideas', defaultMessage: 'Ideas' }) }
    ],
    [intl]
  );

  const buildTableRows = useCallback(() => {
    return chartData?.themeIdeasSummary?.map((item) => [item.themeName, item.ideaCount]) || [];
  }, [chartData?.themeIdeasSummary]);

  const tableRows = useMemo(() => {
    if (!dataTableViewEnabled) {
      return [];
    }

    return buildTableRows();
  }, [buildTableRows, dataTableViewEnabled]);

  const getExportData = useCallback(() => {
    return { columns: tableColumns, rows: buildTableRows() };
  }, [buildTableRows, tableColumns]);

  if ((loadingChallenges && !challengeList) || (loadingChart && !chartData)) {
    return (
      <Styled.LoadingContainer>
        <LoadingIndicator inline />
      </Styled.LoadingContainer>
    );
  }

  const formatThemeName = (value: string | number) => {
    const dataPoint = chartSeries[0]?.data.find((d) => d.x === value);
    return dataPoint?.themeName || value;
  };

  return (
    <Grid key="ideasByThemeStats">
      <CustomFilters
        userFieldOptions={userFieldOptions}
        communityUserFields={communityUserFields}
        countries={countries}
        userFieldUpdate={(userField: UserFieldType) => {
          // Reset selected theme - means "no selected theme"
          setTheme(ThemeSelectionType.PUBLISHED);
          setUserFieldOptions(userField);
        }}
      />
      <GridHeader key="challengeSelectionHeader">
        {intl.formatMessage({ id: 'show', defaultMessage: 'Show' })}
      </GridHeader>
      <GridRow key="challengeSelection">
        <Select
          defaultValue={challenge}
          options={[
            {
              label: intl.formatMessage({ id: 'allPublishedChallenges', defaultMessage: 'All Published Challenges' }),
              value: ChallengeSelectionType.PUBLISHED,
              'data-testid': `challenge-${ChallengeSelectionType.PUBLISHED}`
            },
            {
              label: intl.formatMessage({ id: 'allChallenges', defaultMessage: 'All Challenges' }),
              value: ChallengeSelectionType.ALL,
              'data-testid': `challenge-${ChallengeSelectionType.ALL}`
            },
            ...(challengeList?.challenges || []).map((challenge: ChallengeListItemType) => ({
              label: challenge.title,
              value: challenge.id,
              'data-testid': `challenge-${challenge.title}`
            }))
          ]}
          onChange={(e) => {
            setChallenge(e.value);
            // And additionally reset a previously selected theme
            setTheme(ThemeSelectionType.PUBLISHED);
          }}
        />
      </GridRow>
      <GridRow key="updateButton">
        <Button
          priority="secondary"
          onClick={() => {
            setThemeIdeasSummaryQueryVariables({
              challengeSelection: challenge,
              themeSelection: ThemeSelectionType.PUBLISHED,
              filters: { userFields: JSON.stringify(userFieldOptions) }
            });
          }}
        >
          {intl.formatMessage({ id: 'update', defaultMessage: 'Update' })}
        </Button>
      </GridRow>
      {themeSummary.length === 0 ? (
        <GridRow key="message">
          <ErrorMessage
            message={intl.formatMessage({
              id: 'thisChallengeHasNoThemes',
              defaultMessage: 'This challenge has no themes'
            })}
            details={intl.formatMessage({
              id: 'pleaseAdjustYourSearch',
              defaultMessage: 'Please adjust your search parameters and try again.'
            })}
          />
        </GridRow>
      ) : (
        <>
          {!dataTableViewEnabled && (
            <ChartActionsRow key="ideasByThemeExport">
              <ChartExportMenu
                chartRef={chartContainerRef}
                fileName={intl.formatMessage({ id: 'ideasByTheme', defaultMessage: 'Ideas by Theme' })}
                formatDate={formatDate}
                getExportData={getExportData}
              />
            </ChartActionsRow>
          )}
          <GridRow key="chart">
            {dataTableViewEnabled ? (
              <TableView
                columns={tableColumns}
                rows={tableRows}
                formatDate={formatDate}
                onRowClick={(index: number) => {
                  const selectedTheme = themeSummary[index];
                  if (selectedTheme?.themeId !== undefined) {
                    setTheme(String(selectedTheme.themeId));
                  }
                }}
                summary={intl.formatMessage({ id: 'ideasByTheme', defaultMessage: 'Ideas by Theme' })}
              />
            ) : (
              <ChartScroll width={chartScrollWidth}>
                <ChartScrollInner minWidth={chartMinWidth} ref={chartContainerRef}>
                  <ThemeProvider theme={accessibleChartTheme}>
                    <BarChart
                      axisX={{
                        label: intl.formatMessage({ id: 'theme', defaultMessage: 'Theme' }),
                        formatTick: formatThemeName,
                        formatTooltipValue: formatThemeName
                      }}
                      axisY={{
                        formatScreenReaderValue: (value) => `${ideasLabel}: ${value}`,
                        label: intl.formatMessage({
                          id: 'numberOfIdeas',
                          defaultMessage: 'Number of ideas'
                        }),
                        formatValue: (value) => {
                          const numericValue = Number(value);
                          return Number.isInteger(numericValue) ? numericValue : '';
                        }
                      }}
                      data={chartSeries}
                      domainScaling="from-zero"
                      hasLegend={false}
                      hasValueLabels
                      onDataClick={(dataPoint) => {
                        if (dataPoint.themeId !== undefined) {
                          setTheme(String(dataPoint.themeId));
                        }
                      }}
                    />
                  </ThemeProvider>
                </ChartScrollInner>
              </ChartScroll>
            )}
          </GridRow>
        </>
      )}
      {isThemeSelected && (
        <GridRow key="table" justify="center">
          {loadingIdeas && !ideasData ? (
            <Styled.LoadingContainer>
              <LoadingIndicator inline />
            </Styled.LoadingContainer>
          ) : ideasData?.themeIdeasAndCount && ideasData.themeIdeasAndCount.count > 0 ? (
            <>
              <GridCol>
                <IdeasTable
                  getColumnSortDirection={getColumnSortDirection}
                  ideasAndCount={ideasData.themeIdeasAndCount}
                  intl={intl}
                  sortIdeas={sortIdeas}
                />
              </GridCol>
              <GridCol>
                {/* Even if there is only 1 page, we should keep the pagination */}
                <Pagination
                  numPages={Math.ceil(ideasData.themeIdeasAndCount.count / IDEAS_PER_PAGE)}
                  activeIndex={Math.ceil(themeIdeasQueryVariables.offset / IDEAS_PER_PAGE)}
                  onPageClick={(page) => {
                    const pageIndex = page.index as number;
                    setThemeIdeasQueryVariables({
                      ...themeIdeasQueryVariables,
                      offset: pageIndex * IDEAS_PER_PAGE
                    });
                  }}
                />
              </GridCol>
            </>
          ) : (
            <ErrorMessage
              message={intl.formatMessage({ id: 'noIdeasWereFound', defaultMessage: 'No ideas were found' })}
              details={intl.formatMessage({
                id: 'pleaseAdjustYourSearch',
                defaultMessage: 'Please adjust your search parameters and try again.'
              })}
            />
          )}
        </GridRow>
      )}
      {isThemeSelected && ideasData && ideasData?.themeIdeasAndCount?.count > 0 && (
        <GridRow key="exportData">
          <IdeasExportButton exportUrl={getExportIdeasUrl()} intl={intl} />
        </GridRow>
      )}
    </Grid>
  );
};
